import { useState } from "react";
import { Buton, Ikon, marka } from "@koclukpanel/ui";
import { BakimBandi } from "./BakimBandi";
import { Guvenlik } from "./Guvenlik";
import { NasilKullanilir } from "./NasilKullanilir";
import { VeliPortali } from "./VeliPortali";
import { VeliHesabim } from "./VeliHesabim";
import { YedekKodBandi } from "./YedekKodBandi";
import type { YedekKodBandi as Bant } from "./yedek-kod-uyarisi";

/**
 * VELİ KABUĞU — F-PAR
 *
 * ================================================================
 * VELİ ARTIK BU UYGULAMADA
 * ================================================================
 * `VeliPortali` 05.09.2026'ya kadar `apps/panel` içindeydi ve panel
 * `ben.roller.includes("PARENT")` ile hangi ekranı çizeceğine karar
 * veriyordu. Şartname iki uygulamayı zaten ayırıyordu (*"personel
 * paneli ve öğrenci/veli PWA"*), kod tersini yapıyordu — C14.31.
 *
 * Taşımanın ölçülmüş bir güvenlik faydası var: velinin tarayıcısı
 * artık **bütün yönetim panelinin kodunu** indirmiyor. Veri
 * sızmıyordu (yetki her istekte sunucuda) ama veliye personel
 * uygulamasının tam haritası veriliyordu: 41 ekranın adı, akışı,
 * alan yapısı.
 *
 * ================================================================
 * DİL: VELİYE "SİZ", ÖĞRENCİYE "SEN"
 * ================================================================
 * Bu uygulamanın artık tek bir dili yok. `VeliPortali` panel
 * dilindeydi ve **öyle kaldı** — veli öğrenci değil. Öğrenciye özel
 * ekranlar "sen" demeye devam ediyor.
 *
 * Ortak ekranlar "siz" der: onları kimin göreceği bilinmiyor ve
 * bilinmezlikte yanlış olmayan taraf seçilir. Bu kural 05.09.2026'da
 * burada YAZILDI ama UYGULANMADI — ölçüldüğünde giriş akışının tamamı
 * hâlâ "sen" diyordu ("Çıkış yaptın", "Sana bağlantı göndereceğiz",
 * "Uygulamandaki 6 haneli kod"). Yazılı bir kuralın kendisi bir
 * uygulama değil; günün tekrar eden sınıfı.
 *
 * Kural artık `Giris.tsx` başlığında, bağladığı dosya listesiyle ve
 * metinleri harfiyen sabitleyen testlerle birlikte duruyor.
 */
export function VeliKabugu({
  gecisVar,
  onOgrenciyeGec,
  onCikis,
  yedekKodBant,
  onYedekKodKapat,
}: {
  /** Kişi hem öğrenci hem veli mi — geçiş yalnızca o zaman çiziliyor. */
  gecisVar: boolean;
  onOgrenciyeGec: () => void;
  onCikis: () => void;
  /**
   * ================================================================
   * BU İKİ ALAN TAŞIMANIN DÜŞÜRDÜĞÜ KORUMA — ÖLÇÜLDÜ 05.09.2026
   * ================================================================
   * Veli panelde `<VeliPortali />` olarak çizilirken **bakım kapısının
   * arkasındaydı**: `apps/panel/src/Uygulama.tsx` (taşımadan önceki
   * hâli) satır 872'de `if (bakim.data?.acik) return <BakimModu />`
   * vardı ve portal 945'te, yani kapının arkasında geliyordu.
   *
   * Taşınınca o kapı geride kaldı. Öğrenci uygulamasında bakım bandı
   * `OgrenciKabugu`nun **içinde** duruyor (`Kabuk.tsx`), bu kabuk ise
   * ayrı bir ağaç — yani veli bakım sırasında hiçbir şey görmüyor,
   * yalnızca her ekranda 503'ten gelen genel hatayı görüyordu. Tam
   * olarak `BakimBandi`nin öğrenci için yazılma sebebi.
   *
   * Aynısı yedek kod bandında: `Uygulama.tsx` `bant`ı hesaplıyor ama
   * yalnızca öğrenci kabuğuna veriyordu. Yedek koduyla giren veli kaç
   * kodu kaldığını hiç öğrenemiyordu; kodları bitince hesabını
   * **yalnızca yönetici** açabiliyor (G-AUT-03).
   *
   * DERS: bir ekranı taşırken sorulacak soru "bu ekranı kim çiziyordu"
   * değil, **"bu ekran neyin İÇİNDEYDİ"**. Kabuk, içindekine görünmeyen
   * sözler verir.
   */
  yedekKodBant: Bant | null;
  onYedekKodKapat: () => void;
}) {
  /*
    G-AUT-08a (A9, 05.10.2026) — VELİNİN HİÇ GÜVENLİK EKRANI YOKTU: şifre
    değiştirme, açık oturumlar ve 2FA öğrenci kabuğunun "Hesabım"ındaydı ve
    bu kabuk ayrı bir ağaç. Öğrencinin `Guvenlik` ekranı aynen açılıyor;
    hangi politikanın geçerli olduğunu (2FA) yine sunucu söylüyor.
  */
  /*
    NASIL KULLANILIR (yardım aşama 1, 05.10.2026) güvenlikle AYNI desen:
    üst banttaki düğme ekranı açar, açıkken "Çocuğum" olur ve portala
    döndürür. Tek durum değişkeni: iki ayrı bayrak ikisinin birden açık
    kaldığı bir durumu mümkün kılardı.
  */
  const [ekran, setEkran] = useState<"portal" | "guvenlik" | "yardim" | "hesap">("portal");
  const ac = (hedef: "guvenlik" | "yardim" | "hesap") =>
    setEkran((simdiki) => (simdiki === hedef ? "portal" : hedef));
  return (
    /*
      9b: öğrenci kabuğuyla AYNI görsel dil (`ogrenci-yuzeyi`, stil.css) — veli aynı
      telefonda çocuğunun ekranıyla kendi ekranı arasında geçiyor; iki ayrı görünüm
      iki ayrı uygulama gibi duruyordu. Veri ve uçlar DEĞİŞMEDİ: veli yalnız kendi
      çocuğunun verisini görüyor (VeliPortali, sunucu kapsamı).
    */
    <div className="ogrenci-yuzeyi text-notr-900 flex min-h-dvh flex-col bg-(image:--gradyan-unk-zemin) bg-fixed">
      {/* Öğrenci kabuğundaki ile AYNI konum: her şeyin üstünde. */}
      <BakimBandi />
      {/*
        Dar ekranda başlık KESİLMİYOR (UI/UX Dalga 1; GCP 390 px görüntüsünde
        "Çoc…" çıktı): dört düğme başlıkla aynı satırda yer kavgası yapıyordu.
        Başlık kendi satırında tam, düğmeler altında; geniş ekranda eski düzen.
      */}
      <div className="text-menu-metin flex items-center gap-2 bg-(image:--gradyan-unk-menu) px-4 py-2.5">
        <span className="truncate text-lg font-extrabold tracking-[0.02em] uppercase">
          {marka.kisaAd}
        </span>
        <span className="bg-menu-aktif ring-menu-halka ml-auto rounded-full px-2.5 py-0.5 text-xs font-bold ring-1 ring-inset">
          VELİ
        </span>
      </div>
      <header className="border-mor-kenar bg-notr-0/90 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b px-4 py-3 backdrop-blur">
        <h1 className="text-metin-koyu flex shrink-0 items-center gap-2.5 text-lg font-bold max-sm:w-full">
          <span
            aria-hidden="true"
            className="text-notr-0 grid size-9 place-items-center rounded-[0.75rem] bg-(image:--gradyan-unk-birincil) shadow-(--golge-unk-birincil)"
          >
            <Ikon ad="ogrenci" className="size-5" />
          </span>
          Çocuğum
        </h1>
        {/* Dar ekranda düğmeler alt satıra kırılır; yatay taşma yerine. */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/*
            GEÇİŞ YALNIZCA İKİSİ BİRDEN OLAN KİŞİYE.

            Tek kipi olan veliye "Kendi programım" düğmesi göstermek,
            basıldığında boş bir ekran açardı — olmayan bir seçim
            sunmak. Karar `Uygulama.tsx` içinde veriliyor; burası
            yalnızca çiziyor.
          */}
          {gecisVar && (
            <Buton gorunum="ikincil" onClick={onOgrenciyeGec}>
              Kendi programım
            </Buton>
          )}
          <Buton gorunum="sessiz" onClick={() => ac("yardim")}>
            {ekran === "yardim" ? "Çocuğum" : "Nasıl kullanılır"}
          </Buton>
          <Buton gorunum="sessiz" onClick={() => ac("guvenlik")}>
            {ekran === "guvenlik" ? "Çocuğum" : "Şifre ve güvenlik"}
          </Buton>
          <Buton gorunum="sessiz" onClick={onCikis}>
            Çıkış
          </Buton>
          <Buton gorunum="sessiz" onClick={() => ac("hesap")}>
            {ekran === "hesap" ? "Çocuğum" : "Hesabım"}
          </Buton>
        </div>
      </header>

      {/*
        Bant `main`in ÜSTÜNDE ve genişlik sınırının DIŞINDA: öğrenci
        kabuğunda da içeriğin en başında duruyor. Portalın içine
        koysaydık, çocuk seçimi yapılana kadar görünmezdi.
      */}
      <div className="mx-auto w-full max-w-[720px] lg:max-w-[1100px]">
        <YedekKodBandi bant={yedekKodBant} onKapat={onYedekKodKapat} />
      </div>

      <main className="mx-auto w-full max-w-[720px] flex-1 p-4 lg:max-w-[1100px] lg:px-8">
        {ekran === "hesap" ? (
          <VeliHesabim />
        ) : ekran === "guvenlik" ? (
          <Guvenlik />
        ) : ekran === "yardim" ? (
          // Ekran kendi dolgusunu taşıyor; `main`in dolgusu iki kez uygulanmasın.
          <div className="-m-4">
            <NasilKullanilir />
          </div>
        ) : (
          <VeliPortali />
        )}
      </main>
    </div>
  );
}
